import { UserSettings, ViewType } from '../../types';
import { saveViewType, UserKeys } from '../api/users-api';
import { useQueryClient } from '@tanstack/react-query';
import { useStore } from '../components/state/zustand-store';
import { useAnalytics } from './analytics/create-events';
import * as analytics from '../shared/analytics/analytics.json';

import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Bookmark from '@mui/icons-material/Bookmark';
import BookmarkBorder from '@mui/icons-material/BookmarkBorder';
import type { SxProps, Theme } from '@mui/material/styles';

interface BookmarkButtonProps {
  bookmarkKey: string;
  sx?: SxProps<Theme>;
}

export default function BookmarkButton({
  bookmarkKey,
  sx,
}: BookmarkButtonProps) {
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();

  const userSettings = queryClient.getQueryData<UserSettings>([
    UserKeys.UserSettings,
  ]);

  const bookmarks = userSettings?.viewType?.bookmarks || [];
  const hasBookmark = bookmarks.includes(bookmarkKey);

  if (hasBookmark) {
    return (
      <Tooltip title="Remove Bookmark" arrow>
        <IconButton
          sx={sx}
          onClick={() => {
            saveViewType({
              ...userSettings?.viewType,
              bookmarks: bookmarks.filter((b) => b !== bookmarkKey),
            } as ViewType);

            queryClient.setQueryData([UserKeys.UserSettings], {
              ...userSettings,
              viewType: {
                ...userSettings?.viewType,
                bookmarks: bookmarks.filter((b) => b !== bookmarkKey),
              },
            });

            createEvent(analytics.view_settings.bookmarks.delete_bookmark, {
              bookmarkKey,
            });

            setSnackbar({
              open: true,
              message: 'Bookmark removed!',
              severity: 'success',
            });
          }}
        >
          <Bookmark />
        </IconButton>
      </Tooltip>
    );
  } else {
    return (
      <Tooltip title="Bookmark Page" arrow>
        <IconButton
          sx={sx}
          onClick={() => {
            saveViewType({
              ...userSettings?.viewType,
              bookmarks: [...bookmarks, bookmarkKey],
            } as ViewType);

            queryClient.setQueryData([UserKeys.UserSettings], {
              ...userSettings,
              viewType: {
                ...userSettings?.viewType,
                bookmarks: [...bookmarks, bookmarkKey],
              },
            });

            createEvent(analytics.view_settings.bookmarks.create_bookmark, {
              bookmarkKey,
            });

            setSnackbar({
              open: true,
              message: 'Bookmark added!',
              severity: 'success',
            });
          }}
        >
          <BookmarkBorder />
        </IconButton>
      </Tooltip>
    );
  }
}
